import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://khyros.ai';

interface PageMeta {
  title: string;
  description: string;
  // Path search engines should treat as the original (e.g. /home -> /)
  canonicalPath?: string;
}

const HOME_META: PageMeta = {
  title: 'Khyros | Geospatial Intelligence Powered by AI',
  description:
    'Khyros transforms complex geospatial data into actionable intelligence, using AI to identify patterns, anomalies, and future events with verifiable accuracy.',
  canonicalPath: '/',
};

// Keep in sync with the routes in App.tsx, public/sitemap.xml and scripts/prerender.js
export const PAGE_META: Record<string, PageMeta> = {
  '/': HOME_META,
  '/home': HOME_META,
  '/terms': {
    title: 'Terms of Service | Khyros',
    description: 'The Khyros Terms of Service governing use of the Khyros geospatial intelligence platform.',
  },
  '/privacy': {
    title: 'Privacy Statement | Khyros',
    description: 'How Khyros collects, uses, and protects personal information.',
  },
  '/government-amendment-to-terms': {
    title: 'Government Amendment to Terms of Service | Khyros',
    description: 'Amendment to the Khyros Terms of Service applicable to U.S. Federal Government users.',
  },
  '/requests': {
    title: 'Guidelines for Legal Requests of User Data | Khyros',
    description: 'Guidelines for law enforcement and government requests for Khyros user data.',
  },
  '/eula': {
    title: 'End User License Agreement | Khyros',
    description: 'The Khyros software End User License Agreement.',
  },
};

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

// Updates <title>, description, canonical and Open Graph tags on navigation.
// The prerender step (scripts/prerender.js) snapshots the result into static HTML.
export const usePageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const meta = PAGE_META[path] || HOME_META;
    const url = SITE_URL + (meta.canonicalPath ?? path);

    document.title = meta.title;
    setMeta('meta[name="description"]', 'content', meta.description);
    setMeta('meta[property="og:title"]', 'content', meta.title);
    setMeta('meta[property="og:description"]', 'content', meta.description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[name="twitter:title"]', 'content', meta.title);
    setMeta('meta[name="twitter:description"]', 'content', meta.description);
    setMeta('link[rel="canonical"]', 'href', url);
  }, [pathname]);
};
